import styled from '@emotion/styled'
import { Box } from '@mui/material'
import React from 'react'

import { ImageGallery } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGallery'
import { HouseDetailProvider } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { Breadcrumbs } from '@f-fertighaus/applications/Next/views/houseDetailPage/desktop/Breadcrumbs/Breadcrumbs'
import { HouseImagesSection } from '@f-fertighaus/applications/Next/views/houseDetailPage/desktop/HouseImagesSection'
import { ReturnToHouseSearchButton } from '@f-fertighaus/applications/Next/views/houseDetailPage/desktop/ReturnToHouseSearchButton/ReturnToHouseSearchButton'
import { StickyCTA } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseDetailPage.styled'
import { HouseImageCarousel } from '@f-fertighaus/applications/Next/views/houseDetailPage/houseImageCarousel/HouseImageCarousel'
import { HouseImageGallery } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/HouseImageGallery'
import { HouseOverview } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseOverview'
import { HouseSections } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/HouseSections'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

import { ChatAssistantWrapper } from '../../components/common/ChatAssistantWrapper'
import { HouseDetailTabsProvider } from '../../components/common/navigation/tabs/useActiveTabs'
import { InactiveVendorHouses } from '../../InactiveVendorHouses/InactiveVendorHouses'
import { HouseDetail } from '../../types/houseDetailsResponse'
import { VendorDetails } from '../../types/vendorResponse'
import { StickySidebar } from '../../views/houseDetailPage/desktop/StickySidebar/StickySidebar'

export type Domain =
  | 'fertighaus'
  | 'massivhaus'
  | 'einfamilienhaus'
  | 'bungalow'
  | 'bauen'
  | 'immowelt'
  | 'immonet'

interface HDPContentProps {
  houseData: HouseDetail
  vendorData: VendorDetails
  domain: Domain
  reviewsActive: boolean
  chatAssistantActive: boolean
  featureFlags: string[]
}

export const HDPContent = ({
  houseData,
  vendorData,
  domain,
  reviewsActive,
  chatAssistantActive,
  featureFlags
}: HDPContentProps) => {
  return (
    <HouseDetailProvider
      data={houseData}
      vendor={vendorData}
      domain={domain}
      reviewsActive={reviewsActive}
      featureFlags={featureFlags}>
      <PageContainer domain={domain}>
        {chatAssistantActive && <ChatAssistantWrapper />}
        <HDPContainer displayPrint='none'>
          <ClientOnly>
            <InactiveVendorHouses />
          </ClientOnly>
          <ImageGallery.ContextProvider>
            <HouseImageCarousel />
            <HouseImageGallery />
            <HDPGridLayout>
              <Breadcrumbs />
              <HouseImagesSection />
              <HouseDetailTabsProvider>
                <HouseOverview />
                <HouseSections />
                <ClientOnly>
                  <StickySidebar />
                </ClientOnly>
              </HouseDetailTabsProvider>
              <ClientOnly>
                <ReturnToHouseSearchButton />
              </ClientOnly>
            </HDPGridLayout>
            <StickyCTA />
          </ImageGallery.ContextProvider>
        </HDPContainer>
      </PageContainer>
    </HouseDetailProvider>
  )
}

const PageContainer = styled(Box)<{ domain: Domain }>`
  width: 100%;
  background-color: ${({ theme, domain }) =>
    domain === 'massivhaus'
      ? theme.palette.common.white
      : theme.palette.glass[500]};
`

const HEADER_FOOTER_WIDTH_DIFF_PX = 40

const HDPContainer = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: calc(
      ${({ theme }) => theme.breakpoints.values['lg']}px -
        ${HEADER_FOOTER_WIDTH_DIFF_PX}px
    );
  }
  margin: auto;
  background-color: ${({ theme }) => theme.palette.common.white};
`

const HDPGridLayout = styled(Box)`
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: grid;
    grid-template-areas: 'breadcrumbs breadcrumbs' 'header header' 'gallery gallery' 'main-content sidebar' 'footer footer';
    grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
    grid-column-gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(3)}
      ${({ theme }) => theme.spacing(4)};
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    padding: ${({ theme }) => theme.spacing(3)}
      ${({ theme }) => theme.spacing(4)};
  }
  label: HDPGridLayout;
`
